/**
 * Waiver check utilities: load template and validate stored waiver data
 * (used to decide whether to skip the waiver page when already signed in store)
 */
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase.config";
import { getSettingsPath } from "./dbPaths";

/**
 * Load waiver template from Firestore (same doc as RentalWaiver/EquipmentWaiver).
 * @returns {Promise<object|null>} Template or null if missing/error
 */
export async function loadWaiverTemplate() {
  try {
    const settingsPath = getSettingsPath();
    const templateRef = doc(db, settingsPath, "waiverTemplate");
    const templateDoc = await getDoc(templateRef);
    if (!templateDoc.exists()) return null;
    return templateDoc.data();
  } catch (err) {
    console.error("waiverCheck loadWaiverTemplate:", err);
    return null;
  }
}

/**
 * Validate stored waiver data against template (same rules as waiver canSign()).
 * @param {object} waiverData - From useNewBookingStore (typedName, homeAddress, fullSignature, etc.)
 * @param {object} template - Waiver template with sections and finalSignatureSection
 * @returns {boolean}
 */
export function validateStoredWaiver(waiverData, template) {
  if (!waiverData || !template) return false;

  const nameValid =
    !template.finalSignatureSection?.requiresTypedName ||
    (waiverData.typedName && String(waiverData.typedName).trim());

  const addressValid =
    !template.finalSignatureSection?.requiresHomeAddress ||
    (waiverData.homeAddress &&
      String(waiverData.homeAddress).trim().length >= 7);

  const signatureValid =
    !template.finalSignatureSection?.requiresSignature ||
    !!waiverData.fullSignature;

  // Per-section initials are no longer collected — only the final signature.
  // Note: templates already in Firestore may still carry requiresInitial on their
  // sections. That flag is now inert; checking it here would make every stored
  // waiver fail validation and force customers to re-sign on every booking.
  return nameValid && addressValid && signatureValid;
}
